import { useState } from 'react'
import { X } from 'lucide-react'

/**
 * Status Change Modal Component
 * نافذة تغيير حالة الفاتورة
 *
 * Features:
 * - 4 status options (draft, pending, paid, cancelled)
 * - Radio button selection
 * - Confirm/Cancel actions
 * - Modal backdrop with click-outside to close
 *
 * Protocols Applied:
 * - P-YNEX-FORM-001: Form structure
 * - P-YNEX-CSS-001: Tailwind CSS usage
 */
const StatusChangeModal = ({ currentStatus, onConfirm, onCancel }) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus)

  // Status options with Arabic labels
  const statuses = [
    {
      value: 'draft',
      label: 'مسودة',
      description: 'فاتورة غير مكتملة',
      color: 'text-gray-700 dark:text-gray-300'
    },
    {
      value: 'pending',
      label: 'قيد الانتظار',
      description: 'في انتظار الدفع',
      color: 'text-yellow-700 dark:text-yellow-300'
    },
    {
      value: 'paid',
      label: 'مدفوعة',
      description: 'تم الدفع بالكامل',
      color: 'text-green-700 dark:text-green-300'
    },
    {
      value: 'cancelled',
      label: 'ملغية',
      description: 'فاتورة ملغية',
      color: 'text-red-700 dark:text-red-300'
    }
  ]

  const handleConfirm = () => {
    if (selectedStatus !== currentStatus) {
      onConfirm(selectedStatus)
    } else {
      onCancel()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            تغيير حالة الفاتورة
          </h3>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Status Options */}
        <div className="space-y-2 mb-6">
          {statuses.map((status) => (
            <label
              key={status.value}
              className={`
                flex items-start gap-3 p-3 border rounded-lg cursor-pointer
                transition-all
                ${selectedStatus === status.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }
              `}
            >
              <input
                type="radio"
                name="status"
                value={status.value}
                checked={selectedStatus === status.value}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-4 h-4 mt-1 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1">
                <p className={`font-medium ${status.color}`}>
                  {status.label}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {status.description}
                </p>
              </div>
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="btn btn-secondary flex-1"
          >
            إلغاء
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedStatus === currentStatus}
            className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {selectedStatus === currentStatus ? 'بدون تغيير' : 'تأكيد التغيير'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StatusChangeModal
